/**
 * Module 00 — Simple Mode · code primitives (CodeHL / CopyBtn / CodeBlock).
 * Copy buttons write the FULL snippet to the clipboard and show transient
 * "Copied" feedback (SM-05).
 */

'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '../icons';

const HL_RE =
  /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(#[^\n]*|\/\/[^\n]*)|\b(import|from|const|let|var|await|async|new|return|def|class|export|true|false|null|None|print|console)\b/g;

export function CodeHL({ code }: { code: string }) {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  HL_RE.lastIndex = 0;
  while ((m = HL_RE.exec(code))) {
    if (m.index > last) nodes.push(code.slice(last, m.index));
    const color = m[1]
      ? 'var(--cs-code-string)'
      : m[2]
        ? 'var(--cs-code-comment)'
        : 'var(--cs-code-keyword)';
    nodes.push(
      <span key={k++} style={{ color }}>
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < code.length) nodes.push(code.slice(last));
  return <code>{nodes}</code>;
}

export function CopyBtn({
  text,
  label = 'Copy',
  className,
}: {
  text: string;
  label?: string;
  className?: string;
}) {
  const [ok, setOk] = React.useState(false);
  return (
    <button
      type="button"
      data-testid="copy-btn"
      aria-label={ok ? 'Copied' : label}
      onClick={() => {
        try {
          navigator.clipboard?.writeText(text);
        } catch {
          /* clipboard unavailable — non-fatal */
        }
        setOk(true);
        setTimeout(() => setOk(false), 1400);
      }}
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-cs-border px-2 py-1 text-[11px] font-medium text-cs-text-secondary transition-colors hover:bg-cs-surface-hover',
        ok && 'border-cs-success-500/30 text-cs-success-400',
        className,
      )}
    >
      <Icon name={ok ? 'check' : 'copy'} size={13} />
      {ok ? 'Copied' : label}
    </button>
  );
}

export function CodeBlock({ title, lang, code }: { title: string; lang: string; code: string }) {
  return (
    <div className="overflow-hidden rounded-lg border border-cs-border bg-cs-code dark:bg-cs-surface-page">
      <div className="flex items-center gap-2 border-b border-cs-border bg-cs-surface-raised px-3 py-1.5">
        <span className="text-xs font-medium text-cs-text-secondary">{title}</span>
        <span className="ml-auto text-[10px] uppercase text-cs-text-muted">{lang}</span>
        <CopyBtn text={code} />
      </div>
      <pre className="overflow-x-auto p-3 text-[12.5px] leading-relaxed text-cs-text-secondary">
        <CodeHL code={code} />
      </pre>
    </div>
  );
}
